import React, { useEffect } from "react";
import { Navigate, useParams } from "react-router";
import {
  Link,
  NavigationSidebar,
  TreeNode,
  TreeView,
  useSidebarCollapsed,
} from "@sscs/ui";
import {
  ORGANIZATION_CATALOG_ID,
  PERMISSION,
  STORE_CATALOG,
  STORE_CATALOG_PACKAGE_ID,
  STORE_CATALOG_PLATFORM,
} from "Constants";
import { useAllowedTo } from "Hooks";
import { CatalogRoute } from "Types";
import { getCatalogRoute } from "Utils";
import useCatalogs from "../../hooks/useCatalogs";
import { Catalog, CatalogNode, Catalogs } from "../../types";
import { getBrowsableCount, getCountDetails } from "../../utils";

const getEntryPlatform = (
  catalog: Catalog,
  canCurate: boolean,
): CatalogNode => {
  const platforms = Object.values(catalog.platforms);

  return (
    platforms.find((platform) => getBrowsableCount(platform, canCurate) > 0) ??
    platforms[0]
  );
};

const CATALOG_SIDEBAR_STORAGE_KEY = "software-catalog";

export const getCatalogEntryTarget = (
  storedCatalog: string | null,
  storedPlatform: string | null,
  storedPackageId: string | null,
  catalogs: Catalogs,
  canCurate: boolean,
): CatalogRoute => {
  const stored: Catalog | undefined = storedCatalog
    ? catalogs[storedCatalog]
    : undefined;

  if (storedCatalog && storedPlatform && stored?.platforms[storedPlatform]) {
    return storedPackageId
      ? {
          catalogId: storedCatalog,
          platform: storedPlatform,
          packageId: storedPackageId,
        }
      : { catalogId: storedCatalog, platform: storedPlatform };
  } else {
    return {
      catalogId: ORGANIZATION_CATALOG_ID,
      platform: getEntryPlatform(catalogs[ORGANIZATION_CATALOG_ID], canCurate)
        .name,
    };
  }
};

const toTreeNode =
  (canCurate: boolean) =>
  (node: CatalogNode): TreeNode => ({
    id: node.route,
    label: <Link to={node.route}>{node.label}</Link>,
    count: getBrowsableCount(node, canCurate),
    tooltip:
      canCurate && node.total !== null
        ? getCountDetails([node.count, node.total]).join(" · ")
        : undefined,
    children: Object.values(node.children).map(toTreeNode(canCurate)),
  });

const Navigation = (): React.JSX.Element => {
  const { groupId, catalogId, platform, packageId } = useParams();
  const [isCollapsed, setIsCollapsed] = useSidebarCollapsed(
    CATALOG_SIDEBAR_STORAGE_KEY,
  );
  const { catalogs, selectedCatalog } = useCatalogs();
  const canCurateCatalog = useAllowedTo({
    permissions: [PERMISSION.CREATE_PACKAGE],
    groupId,
  });

  useEffect(() => {
    if (selectedCatalog && catalogId && platform) {
      localStorage.setItem(STORE_CATALOG, catalogId);
      localStorage.setItem(STORE_CATALOG_PLATFORM, platform);

      if (packageId) {
        localStorage.setItem(STORE_CATALOG_PACKAGE_ID, packageId);
      } else {
        localStorage.removeItem(STORE_CATALOG_PACKAGE_ID);
      }
    }
  }, [selectedCatalog, catalogId, platform, packageId]);

  if (groupId && catalogs && !catalogId) {
    return (
      <Navigate
        to={getCatalogRoute(
          groupId,
          getCatalogEntryTarget(
            localStorage.getItem(STORE_CATALOG),
            localStorage.getItem(STORE_CATALOG_PLATFORM),
            localStorage.getItem(STORE_CATALOG_PACKAGE_ID),
            catalogs,
            canCurateCatalog,
          ),
        )}
        replace
      />
    );
  }

  const currentCatalog: Catalog | undefined =
    catalogs && catalogId ? catalogs[catalogId] : undefined;

  if (currentCatalog && !platform) {
    return (
      <Navigate
        to={getEntryPlatform(currentCatalog, canCurateCatalog).route}
        replace
      />
    );
  }

  return (
    <NavigationSidebar
      aria-label="software-catalog-navigation"
      storageKey={CATALOG_SIDEBAR_STORAGE_KEY}
      title="Catalogs"
      isCollapsed={isCollapsed}
      onCollapse={setIsCollapsed}
    >
      <TreeView
        selected={selectedCatalog?.path.at(-1)?.route ?? null}
        trees={Object.values(catalogs ?? {}).map((catalog) => ({
          title: catalog.title,
          nodes: Object.values(catalog.platforms).map(
            toTreeNode(canCurateCatalog),
          ),
        }))}
      />
    </NavigationSidebar>
  );
};

export default Navigation;
